"use client";

import { cn } from "@universe/ui/lib/utils";
import React from "react";

import CopyButton from "@/components/copy-button";
import { NoTranslate } from "@/components/no-translate";

interface DetailRowProps {
  label: string;
  value: string | React.ReactNode;
  copyable?: boolean;
  stack?: boolean;
  textClassName?: string;
  labelClassName?: string;
  small?: boolean;
  className?: string;
  /**
   * Pass `false` to wrap `value` in `<NoTranslate>` so browser translation
   * extensions (Chrome translate, etc.) leave the rendered value alone.
   * Omitting the prop or passing `true` keeps the value translatable.
   * Values that must remain byte-exact pass `false`: legal names, IBANs,
   * BICs, account numbers, blockchain addresses, transaction IDs,
   * currency codes, emails, phone numbers, formatted amounts.
   *
   * The label always translates, whatever this prop is set to.
   */
  translateValue?: boolean;
}

export default function DetailRow({
  label,
  value,
  copyable,
  stack,
  textClassName,
  labelClassName,
  className,
  translateValue,
}: DetailRowProps) {
  if (!value) return null;

  const copyableValue = typeof value === "string" ? value : undefined;
  // Omitted or `true`: the value translates. Only `false` opts it out of
  // browser translation by wrapping it in `<NoTranslate>`.
  const shouldNoTranslate = translateValue === false;
  const renderedValue = shouldNoTranslate ? (
    <NoTranslate>{value}</NoTranslate>
  ) : (
    value
  );

  return (
    <div
      className={cn(
        "flex justify-between items-center py-1 gap-1",
        stack && "flex-col justify-start",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2",
          stack && "justify-between w-full text-left"
        )}
      >
        <span
          className={cn("text-sm font-semibold text-gray-900", labelClassName)}
        >
          {label}
        </span>
        {stack && copyable && copyableValue && (
          <CopyButton value={copyableValue} size="xs" />
        )}
      </div>
      <div
        className={cn(
          "flex items-center gap-2 text-base font-normal text-right",
          stack && "justify-between w-full text-left"
        )}
      >
        <div
          className={cn(
            stack ? "mt-1" : "ml-2",
            "[word-break:break-word]",
            textClassName
          )}
        >
          {renderedValue}
        </div>
        {!stack && copyable && copyableValue && (
          <CopyButton value={copyableValue} size="xs" />
        )}
      </div>
    </div>
  );
}
